/*
 * Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT-0
 */
import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { TenantConfigService } from '../views/tenant-config/tenant-config.service';

@Component({
  selector: 'app-tax-calculator-dialog',
  template: `
    <h2 mat-dialog-title>{{ 'TAX_CALCULATOR.TITLE' | translate }}</h2>
    <mat-dialog-content>
      <div class="tax-calculator-fields">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>{{ 'TAX_CALCULATOR.AMOUNT_BEFORE_TAX' | translate }}</mat-label>
          <input matInput type="number" step="0.01" min="0"
            [ngModel]="amountBeforeTaxDisplay"
            (ngModelChange)="onAmountBeforeTaxChange($event)">
          <button mat-icon-button matSuffix type="button" (click)="copyAmount(getAmountBeforeTaxCopyValue())"
            [attr.aria-label]="'TAX_CALCULATOR.COPY' | translate" [matTooltip]="'TAX_CALCULATOR.COPY' | translate">
            <mat-icon class="material-symbols-outlined">content_copy</mat-icon>
          </button>
        </mat-form-field>
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>{{ 'TAX_CALCULATOR.TAX_PERCENT' | translate }}</mat-label>
          <input matInput type="number" step="0.01" min="0" max="100"
            [ngModel]="taxPercent"
            (ngModelChange)="onTaxPercentChange($event)">
        </mat-form-field>
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>{{ 'TAX_CALCULATOR.AMOUNT_AFTER_TAX' | translate }}</mat-label>
          <input matInput type="number" step="0.01" min="0"
            [ngModel]="amountAfterTaxDisplay"
            (ngModelChange)="onAmountAfterTaxChange($event)">
          <button mat-icon-button matSuffix type="button" (click)="copyAmount(getAmountAfterTaxCopyValue())"
            [attr.aria-label]="'TAX_CALCULATOR.COPY' | translate" [matTooltip]="'TAX_CALCULATOR.COPY' | translate">
            <mat-icon class="material-symbols-outlined">content_copy</mat-icon>
          </button>
        </mat-form-field>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>{{ 'TAX_CALCULATOR.CLOSE' | translate }}</button>
    </mat-dialog-actions>
  `,
  styles: [`
    .tax-calculator-fields {
      display: flex;
      flex-direction: column;
      gap: 16px;
      min-width: 280px;
      padding: 8px 0;
    }
    .full-width {
      width: 100%;
    }
  `],
})
export class TaxCalculatorDialogComponent implements OnInit {
  taxPercent: number | null = null;
  amountBeforeTax: number = 0;
  amountAfterTax: number = 0;

  private _amountBeforeTaxDisplay = '';
  private _amountAfterTaxDisplay = '';

  get amountBeforeTaxDisplay(): string {
    return this._amountBeforeTaxDisplay;
  }
  set amountBeforeTaxDisplay(val: string) {
    this._amountBeforeTaxDisplay = val;
  }

  get amountAfterTaxDisplay(): string {
    return this._amountAfterTaxDisplay;
  }
  set amountAfterTaxDisplay(val: string) {
    this._amountAfterTaxDisplay = val;
  }

  constructor(
    public dialogRef: MatDialogRef<TaxCalculatorDialogComponent>,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private tenantConfigSvc: TenantConfigService
  ) {}

  // Initialize tax % from tenant vat-percentage. The nav tax-calculator dialog opens this.
  ngOnInit(): void {
    this.tenantConfigSvc.fetch().subscribe({
      next: (configs) => {
        const match = (configs || []).find((c) => c.configName === 'vat-percentage');
        const parsed = match?.configValue === undefined || match?.configValue === null || match?.configValue === ''
          ? NaN
          : Number(match.configValue);
        if (!Number.isFinite(parsed)) {
          this.snackBar.open(this.translate.instant('TAX_CALCULATOR.VAT_PERCENTAGE_UNAVAILABLE'), '', {
            duration: 4000,
          });
          return;
        }
        this.taxPercent = parsed;
      },
      error: (err) => {
        console.error('Failed to load vat-percentage tenant config', err);
        this.snackBar.open(this.translate.instant('TAX_CALCULATOR.VAT_PERCENTAGE_UNAVAILABLE'), '', {
          duration: 4000,
        });
      },
    });
  }

  private round2(value: number): number {
    return Math.round(value * 100) / 100;
  }

  private formatDisplay(value: number): string {
    if (value === 0) return '';
    return value.toFixed(2);
  }

  onAmountBeforeTaxChange(value: string | number): void {
    const strVal = value === null || value === undefined ? '' : String(value).trim();
    this._amountBeforeTaxDisplay = strVal;
    if (strVal === '') {
      this.amountBeforeTax = 0;
      this.amountAfterTax = 0;
      this._amountAfterTaxDisplay = '';
      return;
    }
    const num = parseFloat(strVal);
    if (isNaN(num) || num < 0) return;
    if (this.taxPercent === null) return;
    this.amountBeforeTax = this.round2(num);
    this.amountAfterTax = this.round2(num * (1 + this.taxPercent / 100));
    this._amountAfterTaxDisplay = this.formatDisplay(this.amountAfterTax);
  }

  onAmountAfterTaxChange(value: string | number): void {
    const strVal = value === null || value === undefined ? '' : String(value).trim();
    this._amountAfterTaxDisplay = strVal;
    if (strVal === '') {
      this.amountBeforeTax = 0;
      this.amountAfterTax = 0;
      this._amountBeforeTaxDisplay = '';
      return;
    }
    const num = parseFloat(strVal);
    if (isNaN(num) || num < 0) return;
    if (this.taxPercent === null) return;
    this.amountAfterTax = this.round2(num);
    this.amountBeforeTax = this.round2(num / (1 + this.taxPercent / 100));
    this._amountBeforeTaxDisplay = this.formatDisplay(this.amountBeforeTax);
  }

  onTaxPercentChange(value: string | number): void {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (!isNaN(num) && num >= 0 && num <= 100) {
      this.taxPercent = num;
      if (this.amountBeforeTax > 0) {
        this.amountAfterTax = this.round2(this.amountBeforeTax * (1 + this.taxPercent / 100));
        this._amountAfterTaxDisplay = this.formatDisplay(this.amountAfterTax);
      }
    }
  }

  getAmountBeforeTaxCopyValue(): string {
    return this.formatDisplay(this.amountBeforeTax) || '0.00';
  }

  getAmountAfterTaxCopyValue(): string {
    return this.formatDisplay(this.amountAfterTax) || '0.00';
  }

  copyAmount(value: string): void {
    if (!value) return;
    const copiedMsg = this.translate.instant('TAX_CALCULATOR.COPIED');
    const failedMsg = this.translate.instant('TAX_CALCULATOR.COPY_FAILED');
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(value).then(
        () => this.snackBar.open(copiedMsg, undefined, { duration: 2000 }),
        () => this.snackBar.open(failedMsg, undefined, { duration: 3000 })
      );
    } else {
      this.snackBar.open(failedMsg, undefined, { duration: 3000 });
    }
  }
}
